import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { resetPassword, getPasswordPolicy } from 'services/API/User';
import useAuth from 'providers/Auth/useAuth';

import Button from 'components/generics/Button';
import { displayErrorMessage } from 'utils/tools_functions';
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

const DEFAULT_FORM = { password: '', passwordVerify: '' };

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&');

const PasswordField = ({
  name,
  label,
  value,
  onChange,
  disabled,
  error,
  helperText,
  visible,
  onToggleVisibility,
}) => (
  <TextField
    fullWidth
    margin="normal"
    label={label}
    name={name}
    placeholder={label}
    type={visible ? 'text' : 'password'}
    value={value}
    onChange={onChange}
    autoComplete="new-password"
    disabled={disabled}
    error={error}
    helperText={helperText}
    slotProps={{
      input: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              // aria-label={visible ? `Masquer ${label}` : `Afficher ${label}`}
              onClick={onToggleVisibility}
              onMouseDown={(event) => event.preventDefault()}
              edge="end"
              sx={{ paddingLeft: 0 }}
            >
              {visible ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      },
    }}
  />
);

const LoginForm = () => {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [token, setToken] = useState(null);
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const [passwordPolicyLoading, setPasswordPolicyLoading] = useState(true);
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    passwordVerify: false,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { logout } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromQuery = params.get('token');
    const actionFromQuery = params.get('action');

    setToken(tokenFromQuery);
    setAction(actionFromQuery);

    if (!tokenFromQuery) {
      toast.error(t('form.passwordRulesUnavailable'));
      setPasswordPolicyLoading(false);
      return;
    }

    getPasswordPolicy(tokenFromQuery)
      .then((response) => {
        setPasswordPolicy(response?.data?.data ?? null);
      })
      .catch(() => {
        setPasswordPolicy(null);
        toast.error(t('form.passwordRulesUnavailable'));
      })
      .finally(() => {
        setPasswordPolicyLoading(false);
      });
  }, [location.search]);

  const passwordPolicyRequirements = useMemo(() => {
    if (!passwordPolicy) return [];

    const requirements = [
      {
        id: 'minLength',
        message: t('form.passwordPolicy.minLength', { count: passwordPolicy.minLength }),
        test: (value) => value.length >= passwordPolicy.minLength,
      },
      {
        id: 'maxLength',
        message: t('form.passwordPolicy.maxLength', { count: passwordPolicy.maxLength }),
        test: (value) => value.length <= passwordPolicy.maxLength,
      },
    ];

    requirements.push({
      id: 'uppercase',
      message: t('form.passwordPolicy.uppercase'),
      test: (value) => /[A-Z]/.test(value),
    });

    requirements.push({
      id: 'lowercase',
      message: t('form.passwordPolicy.lowercase'),
      test: (value) => /[a-z]/.test(value),
    });

    requirements.push({
      id: 'digit',
      message: t('form.passwordPolicy.digit'),
      test: (value) => /\d/.test(value),
    });

    const pattern = new RegExp(`[${escapeRegExp(passwordPolicy.allowedSpecialCharacters)}]`);
    requirements.push({
      id: 'special',
      message: t('form.passwordPolicy.special', {
        characters: passwordPolicy.allowedSpecialCharacters,
      }),
      test: (value) => pattern.test(value),
    });

    return requirements;
  }, [passwordPolicy, t]);

  const passwordPolicyChecks = useMemo(
    () =>
      passwordPolicyRequirements.map((requirement) => ({
        ...requirement,
        satisfied: requirement.test(formData.password),
      })),
    [passwordPolicyRequirements, formData.password],
  );

  const isPasswordValid = passwordPolicyChecks.every((requirement) => requirement.satisfied);
  const firstFailedRequirement = passwordPolicyChecks.find((requirement) => !requirement.satisfied);
  const passwordsMatch = formData.password === formData.passwordVerify;
  const confirmFieldError = formData.passwordVerify.length > 0 && !passwordsMatch;

  const isSubmitDisabled =
    loading ||
    passwordPolicyLoading ||
    !passwordPolicy ||
    !formData.password ||
    !formData.passwordVerify ||
    !isPasswordValid ||
    !passwordsMatch;

  const handleToggleVisibility = (field) => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const updateForm = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = (e) => {
    if (e) e.preventDefault();

    if (passwordPolicyLoading || !passwordPolicy) {
      toast.error(PASSWORD_POLICY_ERROR_MESSAGE);
      return;
    }

    if (!token) {
      toast.error(t('form.passwordResetBadLink'));
      return;
    }

    if (!isPasswordValid) {
      toast.error(firstFailedRequirement?.message ?? t('form.passwordNotConform'));
      return;
    }

    if (!passwordsMatch) {
      toast.error(displayErrorMessage('ERR_DIFFERENT_PASSWORD'));
      return;
    }

    setLoading(true);
    resetPassword(token, formData)
      .then(() => {
        logout();
        toast.success(t('success.passwordUpdated'));
        navigate('/login');
      })
      .catch((error) => {
        const code = error?.response?.data?.data?.code;
        if (code) {
          toast.error(displayErrorMessage(`ERR_${code}`));
        } else {
          toast.error(error?.response?.data?.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const inputsDisabled = loading || passwordPolicyLoading || !passwordPolicy;

  return (
    <Box>
      <form onSubmit={onSubmit} noValidate>
        <PasswordField
          name="password"
          placeholder={t('form.password')}
          label={t('form.password')}
          value={formData.password}
          onChange={(event) => updateForm('password', event.target.value)}
          disabled={inputsDisabled}
          error={!passwordPolicyLoading && formData.password.length > 0 && !isPasswordValid}
          visible={passwordVisibility.password}
          onToggleVisibility={() => handleToggleVisibility('password')}
        />

        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('form.passwordRules')} :
          </Typography>
          {passwordPolicyLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {t('form.passwordRulesLoading')}
              </Typography>
            </Box>
          ) : !passwordPolicy ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {t('form.passwordRulesUnavailable')}
            </Typography>
          ) : (
            <List dense sx={{ mt: 1 }}>
              {passwordPolicyChecks.map((requirement) => (
                <ListItem
                  key={requirement.id}
                  disableGutters
                  sx={{
                    color: requirement.satisfied ? 'success.main' : 'error.main',
                    py: 0.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      color: requirement.satisfied ? 'success.main' : 'error.main',
                    }}
                  >
                    {requirement.satisfied ? (
                      <CheckCircleOutlineIcon fontSize="small" />
                    ) : (
                      <CancelOutlinedIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={requirement.message}
                    slotProps={{ primary: { variant: 'body2' } }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <PasswordField
          name="passwordVerify"
          placeholder={t('form.confirmPassword')}
          label={t('form.confirmPassword')}
          value={formData.passwordVerify}
          onChange={(event) => updateForm('passwordVerify', event.target.value)}
          disabled={inputsDisabled}
          error={confirmFieldError}
          helperText={confirmFieldError ? displayErrorMessage('ERR_DIFFERENT_PASSWORD') : ' '}
          visible={passwordVisibility.passwordVerify}
          onToggleVisibility={() => handleToggleVisibility('passwordVerify')}
        />

        <Box sx={{ mt: 2 }}>
          <Button
            defaultStyle="d-block"
            type="submit"
            text={action === 'reset' ? t('button.changePassword') : t('button.confirmPassword')}
            disabled={isSubmitDisabled}
          />
        </Box>
      </form>
    </Box>
  );
};

export default LoginForm;
